import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  CalendarClock,
  CalendarDays,
  CalendarHeart,
  Camera,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Coffee,
  FileSignature,
  GraduationCap,
  IdCard,
  ListChecks,
  LogIn,
  LogOut,
  MapPin,
  Megaphone,
  MessagesSquare,
  Palmtree,
  PenLine,
  Play,
  Trash2,
  UserRound,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import BottomNav from "../components/BottomNav";
import BrandLockup from "../components/brand/BrandLockup";
import PlaceholderPortrait from "../components/brand/PlaceholderPortrait";
import Sparkle from "../components/brand/Sparkle";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import type { ClockStatusResponse } from "../types";
import { formatLeaveTypeName } from "../utils/formatters";

/* ── API shapes ───────────────────────────────────────────────────────────── */

interface HomeProfile {
  employee_id: number;
  employee_number: string;
  name: string;
  first_name: string;
  job_title: string | null;
  department_name: string | null;
  site_name: string | null;
  region_code: string | null;
  region_name: string | null;
  manager_name: string | null;
  start_date: string;
  photo_path: string | null;
  working_days: number[];
}

interface HomeLeave {
  year: number;
  leave_type_name: string | null;
  leave_type_code: string | null;
  total_days: string;
  used_days: string;
  remaining_days: string;
  pending_days: string;
  next_start_date: string | null;
  next_end_date: string | null;
  next_leave_type_name: string | null;
}

interface HomeAttention {
  documents_to_sign: number;
  training_due: number;
  self_assessments: number;
  colleague_feedback: number;
  manager_reviews: number;
  tasks: number;
  leave_approvals: number;
  leave_amendments: number;
  job_description_to_sign?: number;
}

interface HomeSummary {
  profile: HomeProfile;
  leave: HomeLeave | null;
  attention: HomeAttention;
}

type EntryKind = "holiday" | "event" | "leave";

interface CalendarEntry {
  kind: EntryKind;
  title: string;
  start_date: string;
  end_date: string;
  colour: string | null;
  pending: boolean;
  description: string | null;
}

interface HomeCalendar {
  year: number;
  month: number;
  working_days: number[];
  entries: CalendarEntry[];
}

interface AnnouncementRow {
  id: number;
  title: string;
  content: string;
  published_at: string | null;
  is_pinned?: boolean;
  read?: boolean;
}

interface PhotoResult {
  ok: boolean;
  photo_path: string | null;
}

/* ── Small helpers ────────────────────────────────────────────────────────── */

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Parse a YYYY-MM-DD string as a local calendar date (no timezone shift). */
function parseDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function dayKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Monday = 0 … Sunday = 6, matching the API's working_days. */
function weekdayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

function formatDays(value: string | number): string {
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? `${n}` : n.toFixed(1);
}

function formatDuration(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function tenureParts(startIso: string, now: Date): { years: number; months: number } {
  const start = parseDay(startIso);
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  months = Math.max(0, months);
  return { years: Math.floor(months / 12), months: months % 12 };
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { employee, refresh } = useAuth();
  const { isEnabled } = useFeatures();
  // Employee records default to plain "en", which Intl formats the American
  // way; use the language i18next actually resolved (en-GB unless en-US/es).
  const resolved = i18n.resolvedLanguage || i18n.language || "en-GB";
  const lang = resolved === "en" ? "en-GB" : resolved;

  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [clock, setClock] = useState<ClockStatusResponse | null>(null);
  const [clockFailed, setClockFailed] = useState(false);
  const [announcement, setAnnouncement] = useState<AnnouncementRow | null>(null);
  const [newsLoaded, setNewsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const load = useCallback(async () => {
    const [s, c, a] = await Promise.allSettled([
      api.get<HomeSummary>("/api/home"),
      api.get<ClockStatusResponse>("/api/clock/status"),
      api.get<AnnouncementRow[]>("/api/announcements"),
    ]);
    if (s.status === "fulfilled") {
      setSummary(s.value);
      setLoadError(false);
    } else {
      setLoadError(true);
    }
    if (c.status === "fulfilled") {
      setClock(c.value);
      setClockFailed(false);
    } else {
      setClockFailed(true);
    }
    if (a.status === "fulfilled") {
      setAnnouncement(a.value[0] ?? null);
    }
    setNewsLoaded(true);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const firstName =
    summary?.profile.first_name || employee?.name.split(" ")[0] || "";
  const hour = now.getHours();
  const greetingKey =
    hour < 12
      ? "home.greetingMorning"
      : hour < 18
        ? "home.greetingAfternoon"
        : "home.greetingEvening";

  const longDate = useMemo(
    () =>
      new Intl.DateTimeFormat(lang, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(now),
    [lang, now],
  );

  const onPhotoChanged = useCallback(
    async (path: string | null) => {
      setSummary((prev) =>
        prev ? { ...prev, profile: { ...prev.profile, photo_path: path } } : prev,
      );
      // Keep the cached "me" record in step so other screens show the photo.
      await refresh();
    },
    [refresh],
  );

  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-pme-paper safe-x">
      {/* Soft brand wash behind the header — the deck's blues and lilac, with
          no awning stripes. Purely decorative. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[340px] bg-gradient-to-b from-pme-sky via-[#eef4fd] to-pme-paper"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-pme-lilac/25 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-pme-light-blue/30 blur-3xl"
      />

      <div className="relative mx-auto max-w-6xl px-4 pb-28 sm:px-6 lg:px-8">
        {/* Brand bar */}
        <header className="safe-top pb-2 pr-24 sm:pr-28">
          <BrandLockup />
        </header>

        {/* Welcome */}
        <section className="pb-5 pt-5 sm:pt-7">
          <p className="font-hand text-xl leading-none text-pme-lilac sm:text-2xl">
            {t("home.tagline")}
          </p>
          <h1 className="mt-1 font-display text-[44px] leading-[0.95] tracking-wide text-pme-blue sm:text-6xl">
            {t(greetingKey, { name: firstName })}
          </h1>
          <p className="mt-2 text-sm font-medium text-pme-muted first-letter:uppercase">
            {longDate}
          </p>
        </section>

        {loadError && (
          <p
            role="alert"
            className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            {t("home.loadError")}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
          {/* Left column — who you are */}
          <div className="lg:col-span-4">
            <ProfileCard
              profile={summary?.profile ?? null}
              fallbackName={employee?.name ?? ""}
              now={now}
              lang={lang}
              onPhotoChanged={onPhotoChanged}
            />
          </div>

          {/* Right column — your day */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-8 lg:gap-5">
            <TodayCard clock={clock} failed={clockFailed} now={now} lang={lang} />
            <LeaveCard leave={summary?.leave ?? null} loaded={summary !== null} lang={lang} />
            <div className="sm:col-span-2">
              <CalendarCard
                lang={lang}
                regionCode={summary?.profile.region_code ?? null}
              />
            </div>
            <AttentionCard
              attention={summary?.attention ?? null}
              reviewsEnabled={isEnabled("360_reviews_enabled")}
              trainingEnabled={isEnabled("training_enabled")}
            />
            <NewsCard
              announcement={announcement}
              loaded={newsLoaded}
              lang={lang}
              socialEnabled={isEnabled("social_feed_enabled")}
            />
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

/* ── Card shell ───────────────────────────────────────────────────────────── */

function CardTitle({
  icon: Icon,
  children,
  action,
}: {
  icon: LucideIcon;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="eyebrow flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-pme-sky text-pme-blue">
          <Icon size={15} strokeWidth={2} aria-hidden />
        </span>
        {children}
      </h2>
      {action}
    </div>
  );
}

function Skeleton({ className }: { className: string }) {
  return <div aria-hidden className={`animate-pulse rounded-lg bg-gray-100 ${className}`} />;
}

/* ── Profile ──────────────────────────────────────────────────────────────── */

function ProfileCard({
  profile,
  fallbackName,
  now,
  lang,
  onPhotoChanged,
}: {
  profile: HomeProfile | null;
  fallbackName: string;
  now: Date;
  lang: string;
  onPhotoChanged: (path: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const name = profile?.name ?? fallbackName;
  const photo = profile?.photo_path ?? null;

  const startedLabel = useMemo(() => {
    if (!profile) return "";
    const date = new Intl.DateTimeFormat(lang, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(parseDay(profile.start_date));
    const { years, months } = tenureParts(profile.start_date, now);
    const parts: string[] = [];
    if (years > 0) parts.push(t("home.profile.tenureYears", { count: years }));
    if (months > 0) parts.push(t("home.profile.tenureMonths", { count: months }));
    const tenure = parts.length > 0 ? parts.join(" ") : t("home.profile.tenureNew");
    return `${date} · ${tenure}`;
  }, [profile, lang, now, t]);

  const pickFile = () => {
    setError(null);
    setMenuOpen(false);
    fileRef.current?.click();
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      setError(t("home.photo.wrongType"));
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError(t("home.photo.tooLarge"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const res = await api.putFormData<PhotoResult>("/api/home/photo", form);
      await onPhotoChanged(res.photo_path);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status < 500 && err.message
          ? err.message
          : t("home.photo.failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async () => {
    setMenuOpen(false);
    setBusy(true);
    setError(null);
    try {
      await api.delete<PhotoResult>("/api/home/photo");
      await onPhotoChanged(null);
    } catch {
      setError(t("home.photo.failed"));
    } finally {
      setBusy(false);
    }
  };

  const rows: { icon: LucideIcon; label: string; value: string | null }[] = profile
    ? [
        { icon: Briefcase, label: t("home.profile.department"), value: profile.department_name },
        { icon: MapPin, label: t("home.profile.site"), value: profile.site_name },
        { icon: UserRound, label: t("home.profile.manager"), value: profile.manager_name },
        { icon: IdCard, label: t("home.profile.employeeNumber"), value: profile.employee_number },
        { icon: CalendarHeart, label: t("home.profile.started"), value: startedLabel },
      ]
    : [];

  return (
    <section
      aria-label={t("home.profile.title")}
      className="card overflow-hidden lg:sticky lg:top-5"
    >
      <input
        ref={fileRef}
        type="file"
        accept={PHOTO_TYPES.join(",")}
        className="sr-only"
        tabIndex={-1}
        onChange={onFile}
      />

      {/* Photo */}
      <div className="relative m-3 mb-0 aspect-[5/4] overflow-hidden rounded-[18px] bg-pme-sky sm:aspect-[16/9] lg:aspect-square">
        {photo ? (
          <img
            src={photo}
            alt={t("home.photo.alt", { name })}
            className="h-full w-full object-cover"
          />
        ) : (
          <PlaceholderPortrait
            title={t("home.photo.placeholderAlt")}
            className="h-full w-full"
          />
        )}

        {!photo && (
          <button
            type="button"
            onClick={pickFile}
            disabled={busy}
            className="group absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-2xl border border-white/70 bg-white/80 px-3.5 py-2.5 text-left shadow-card backdrop-blur-md transition hover:bg-white disabled:opacity-70"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pme-blue text-white shadow-pop transition group-hover:scale-105">
              <Camera size={18} strokeWidth={2} aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-pme-navy">
                {busy ? t("home.photo.uploading") : t("home.photo.add")}
              </span>
              <span className="block truncate text-xs text-pme-muted">
                {t("home.photo.addHint")}
              </span>
            </span>
          </button>
        )}

        {photo && (
          <div className="absolute bottom-3 right-3">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              disabled={busy}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t("home.photo.change")}
              title={t("home.photo.change")}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/85 text-pme-navy shadow-card backdrop-blur-md transition hover:bg-white disabled:opacity-70"
            >
              <Camera size={18} strokeWidth={2} aria-hidden />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute bottom-12 right-0 w-48 overflow-hidden rounded-2xl border border-pme-line bg-white py-1 text-sm shadow-card-hover"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={pickFile}
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left font-medium text-pme-ink hover:bg-pme-sky/50"
                >
                  <PenLine size={15} strokeWidth={1.75} aria-hidden />
                  {t("home.photo.change")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={removePhoto}
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={15} strokeWidth={1.75} aria-hidden />
                  {t("home.photo.remove")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-5 pt-4">
        {error && (
          <p role="alert" className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}

        <p className="text-sm font-medium text-pme-muted">{name}</p>
        {profile ? (
          <p className="mt-0.5 text-2xl font-extrabold leading-tight tracking-tight text-pme-navy">
            {profile.job_title || t("home.profile.noJobTitle")}
          </p>
        ) : (
          <Skeleton className="mt-2 h-7 w-3/4" />
        )}

        <dl className="mt-4 divide-y divide-pme-line/70 border-t border-pme-line/70">
          {profile
            ? rows
                .filter((r) => r.value)
                .map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.label} className="flex items-start gap-3 py-2.5">
                      <Icon
                        size={16}
                        strokeWidth={1.75}
                        aria-hidden
                        className="mt-0.5 shrink-0 text-pme-blue"
                      />
                      <div className="min-w-0">
                        <dt className="text-[11px] font-semibold uppercase tracking-[0.1em] text-pme-muted">
                          {r.label}
                        </dt>
                        <dd className="text-sm font-medium text-pme-ink">{r.value}</dd>
                      </div>
                    </div>
                  );
                })
            : [0, 1, 2, 3].map((i) => (
                <div key={i} className="py-3">
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
        </dl>

        <Link
          to="/me"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-pme-blue hover:underline"
        >
          {t("home.profile.viewProfile")}
          <ArrowRight size={15} strokeWidth={2} aria-hidden />
        </Link>
        {photo && (
          <p className="mt-3 text-[11px] leading-snug text-pme-muted">
            {t("home.photo.visibility")}
          </p>
        )}
      </div>
    </section>
  );
}

/* ── Today (clock) ────────────────────────────────────────────────────────── */

function TodayCard({
  clock,
  failed,
  now,
  lang,
}: {
  clock: ClockStatusResponse | null;
  failed: boolean;
  now: Date;
  lang: string;
}) {
  const { t } = useTranslation();
  const state = clock?.status ?? "not_clocked_in";

  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }),
    [lang],
  );

  const sinceIso =
    state === "on_break" ? clock?.break_started_at : clock?.clocked_in_at;
  const worked = clock?.clocked_in_at
    ? formatDuration((now.getTime() - new Date(clock.clocked_in_at).getTime()) / 1000)
    : null;

  const tone =
    state === "clocked_in"
      ? { dot: "bg-emerald-500", text: t("home.today.onShift") }
      : state === "on_break"
        ? { dot: "bg-amber-500", text: t("home.today.onBreak") }
        : { dot: "bg-gray-300", text: t("home.today.offShift") };

  return (
    <section aria-label={t("home.today.title")} className="card flex flex-col p-5">
      <CardTitle icon={CalendarClock}>{t("home.today.title")}</CardTitle>

      {clock === null && !failed ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : failed ? (
        <p className="text-sm text-pme-muted">{t("home.today.unavailable")}</p>
      ) : (
        <>
          <p className="flex items-center gap-2.5 text-lg font-bold text-pme-navy">
            <span className="relative flex h-2.5 w-2.5">
              {state !== "not_clocked_in" && (
                <span
                  className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
                    state === "clocked_in" ? "bg-emerald-400" : "bg-amber-400"
                  }`}
                />
              )}
              <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${tone.dot}`} />
            </span>
            {tone.text}
          </p>
          <p className="mt-3 font-display text-6xl leading-none tracking-wider text-pme-navy tabular-nums">
            {timeFmt.format(now)}
          </p>
          <p className="mt-2 min-h-[20px] text-sm text-pme-muted">
            {state !== "not_clocked_in" && sinceIso
              ? [
                  t("home.today.since", { time: timeFmt.format(new Date(sinceIso)) }),
                  worked ? t("home.today.worked", { duration: worked }) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "\u00a0"}
          </p>
        </>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-5">
        {state === "not_clocked_in" && (
          <Link to="/clock?start=clock_in" className="btn-primary">
            <LogIn size={17} strokeWidth={2} aria-hidden />
            {t("clock.clockIn")}
          </Link>
        )}
        {state === "clocked_in" && (
          <>
            <Link to="/clock?start=break_start" className="btn-quiet">
              <Coffee size={17} strokeWidth={2} aria-hidden />
              {t("clock.startBreak")}
            </Link>
            <Link to="/clock?start=clock_out" className="btn-quiet">
              <LogOut size={17} strokeWidth={2} aria-hidden />
              {t("clock.clockOut")}
            </Link>
          </>
        )}
        {state === "on_break" && (
          <Link to="/clock?start=break_end" className="btn-primary">
            <Play size={17} strokeWidth={2} aria-hidden />
            {t("clock.endBreak")}
          </Link>
        )}
        <Link
          to="/clock"
          className="ml-auto inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-pme-blue hover:underline"
        >
          {t("home.today.openClock")}
          <ArrowRight size={15} strokeWidth={2} aria-hidden />
        </Link>
      </div>
    </section>
  );
}

/* ── Leave allowance ──────────────────────────────────────────────────────── */

function LeaveCard({
  leave,
  loaded,
  lang,
}: {
  leave: HomeLeave | null;
  loaded: boolean;
  lang: string;
}) {
  const { t } = useTranslation();

  const rangeFmt = useMemo(
    () => new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" }),
    [lang],
  );

  if (!loaded) {
    return (
      <section className="card p-5">
        <CardTitle icon={Palmtree}>{t("home.leave.title")}</CardTitle>
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="mt-4 h-2.5 w-full" />
      </section>
    );
  }

  if (!leave) {
    return (
      <section className="card p-5">
        <CardTitle icon={Palmtree}>{t("home.leave.title")}</CardTitle>
        <p className="text-sm text-pme-muted">{t("home.leave.notConfigured")}</p>
      </section>
    );
  }

  const total = Math.max(0, Number.parseFloat(leave.total_days) || 0);
  const used = Math.max(0, Number.parseFloat(leave.used_days) || 0);
  const pending = Math.max(0, Number.parseFloat(leave.pending_days) || 0);
  const remaining = Number.parseFloat(leave.remaining_days) || 0;
  const pct = (n: number) => (total > 0 ? Math.min(100, (n / total) * 100) : 0);
  const usedPct = pct(used);
  const pendingPct = Math.min(100 - usedPct, pct(pending));

  let nextOff: string | null = null;
  if (leave.next_start_date && leave.next_end_date) {
    const a = parseDay(leave.next_start_date);
    const b = parseDay(leave.next_end_date);
    nextOff =
      leave.next_start_date === leave.next_end_date
        ? rangeFmt.format(a)
        : `${rangeFmt.format(a)} – ${rangeFmt.format(b)}`;
  }

  return (
    <section aria-label={t("home.leave.title")} className="card flex flex-col p-5">
      <CardTitle icon={Palmtree}>
        {leave.leave_type_code && leave.leave_type_name
          ? formatLeaveTypeName(leave.leave_type_code, leave.leave_type_name)
          : t("home.leave.title")}
      </CardTitle>

      <p className="flex items-baseline gap-2">
        <span className="font-display text-6xl leading-none tracking-wide text-pme-blue">
          {formatDays(remaining)}
        </span>
        <span className="text-sm font-semibold text-pme-navy">
          {t("home.leave.daysLeft", { count: remaining === 1 ? 1 : 2 })}{" "}
          <span className="font-medium text-pme-muted">
            {t("home.leave.in", { year: leave.year })}
          </span>
        </span>
      </p>

      <div
        className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-pme-sky"
        role="img"
        aria-label={`${t("home.leave.used")}: ${formatDays(used)}. ${t("home.leave.pending")}: ${formatDays(pending)}. ${t("home.leave.allowance")}: ${formatDays(total)}.`}
      >
        <span className="h-full bg-pme-blue" style={{ width: `${usedPct}%` }} />
        <span className="h-full bg-pme-lilac" style={{ width: `${pendingPct}%` }} />
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <LeaveStat swatch="bg-pme-blue" label={t("home.leave.used")} value={formatDays(used)} />
        <LeaveStat
          swatch="bg-pme-lilac"
          label={t("home.leave.pending")}
          value={formatDays(pending)}
        />
        <LeaveStat
          swatch="bg-pme-sky ring-1 ring-pme-line"
          label={t("home.leave.allowance")}
          value={formatDays(total)}
        />
      </dl>

      <p className="mt-3 text-sm text-pme-muted">
        <span className="font-semibold text-pme-ink">{t("home.leave.nextOff")}:</span>{" "}
        {nextOff ?? t("home.leave.nothingBooked")}
      </p>

      <div className="mt-auto flex items-center gap-2 pt-4">
        <Link to="/leave?tab=request" className="btn-quiet">
          {t("home.leave.request")}
        </Link>
        <Link
          to="/leave?tab=my"
          className="ml-auto inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-pme-blue hover:underline"
        >
          {t("home.leave.viewAll")}
          <ArrowRight size={15} strokeWidth={2} aria-hidden />
        </Link>
      </div>
    </section>
  );
}

function LeaveStat({
  swatch,
  label,
  value,
}: {
  swatch: string;
  label: string;
  value: string;
}) {
  return (
    <div>
      <dd className="text-base font-bold text-pme-navy">{value}</dd>
      <dt className="mt-0.5 flex items-start gap-1.5 leading-tight text-pme-muted">
        <span aria-hidden className={`mt-[3px] h-2 w-2 shrink-0 rounded-full ${swatch}`} />
        {label}
      </dt>
    </div>
  );
}

/* ── Calendar ─────────────────────────────────────────────────────────────── */

const KIND_STYLE: Record<EntryKind, { dot: string; chip: string }> = {
  event: { dot: "bg-pme-blue", chip: "bg-pme-sky text-pme-navy" },
  holiday: { dot: "bg-pme-lilac", chip: "bg-pme-lilac-soft text-[#7a2f8c]" },
  leave: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-800" },
};

function CalendarCard({ lang, regionCode }: { lang: string; regionCode: string | null }) {
  const { t } = useTranslation();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [data, setData] = useState<HomeCalendar | null>(null);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    api
      .get<HomeCalendar>(`/api/home/calendar?year=${year}&month=${month + 1}`, {
        signal: controller.signal,
      })
      .then((res) => setData(res))
      .catch((err: Error) => {
        if (err.name !== "AbortError") setFailed(true);
      });
    return () => controller.abort();
  }, [year, month]);

  // The US week starts on Sunday; the UK, Spain and South Africa on Monday.
  const sundayFirst = regionCode === "us" || lang === "en-US";

  const weekdayLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: "short" });
    // 1 Jan 2024 was a Monday.
    const monday = new Date(2024, 0, 1);
    const labels = Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(2024, 0, monday.getDate() + i)),
    );
    return sundayFirst ? [labels[6], ...labels.slice(0, 6)] : labels;
  }, [lang, sundayFirst]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(cursor),
    [lang, cursor],
  );

  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const lead = sundayFirst ? first.getDay() : weekdayIndex(first);
    const out: (Date | null)[] = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= daysInMonth; d += 1) out.push(new Date(year, month, d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [year, month, sundayFirst]);

  const isCurrent = data !== null && data.year === year && data.month === month + 1;
  const entries = isCurrent ? data.entries : [];
  const workingDays = useMemo(
    () => new Set(isCurrent ? data.working_days : [0, 1, 2, 3, 4]),
    [isCurrent, data],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const entry of entries) {
      const start = parseDay(entry.start_date);
      const end = parseDay(entry.end_date);
      for (
        let d = new Date(start);
        d <= end;
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
      ) {
        if (d.getMonth() !== month || d.getFullYear() !== year) continue;
        const key = dayKey(d);
        const list = map.get(key);
        if (list) list.push(entry);
        else map.set(key, [entry]);
      }
    }
    return map;
  }, [entries, month, year]);

  const todayKey = dayKey(today);
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(lang, { weekday: "short", day: "numeric", month: "short" }),
    [lang],
  );
  const shortFmt = useMemo(
    () => new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" }),
    [lang],
  );

  const listed = useMemo(() => {
    if (selected) return byDay.get(selected) ?? [];
    return [...entries].sort((a, b) => a.start_date.localeCompare(b.start_date));
  }, [selected, byDay, entries]);

  const move = (delta: number) => {
    setSelected(null);
    setCursor(new Date(year, month + delta, 1));
  };

  const kindLabel = (e: CalendarEntry) =>
    e.kind === "holiday"
      ? t("home.calendar.holiday")
      : e.kind === "event"
        ? t("home.calendar.event")
        : e.pending
          ? t("home.calendar.leavePending")
          : t("home.calendar.leave");

  const selectedDate = selected ? parseDay(selected) : null;

  return (
    <section aria-label={t("home.calendar.title")} className="card p-5">
      <CardTitle
        icon={CalendarDays}
        action={
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
              }}
              className="mr-1 rounded-full border border-pme-line px-3 py-1.5 text-xs font-semibold text-pme-navy hover:bg-pme-sky/50"
            >
              {t("home.calendar.today")}
            </button>
            <button
              type="button"
              onClick={() => move(-1)}
              aria-label={t("home.calendar.previous")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-pme-navy hover:bg-pme-sky/60"
            >
              <ChevronLeft size={18} strokeWidth={2} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => move(1)}
              aria-label={t("home.calendar.next")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-pme-navy hover:bg-pme-sky/60"
            >
              <ChevronRight size={18} strokeWidth={2} aria-hidden />
            </button>
          </div>
        }
      >
        {t("home.calendar.title")}
      </CardTitle>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-5">
        {/* Month grid */}
        <div className="md:col-span-3">
          <p
            className="mb-3 font-display text-3xl leading-none tracking-wide text-pme-navy first-letter:uppercase"
            aria-live="polite"
          >
            {monthLabel}
          </p>
          <div className="grid grid-cols-7 gap-1 text-center">
            {weekdayLabels.map((label) => (
              <div
                key={label}
                className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-pme-muted"
              >
                {label}
              </div>
            ))}
            {cells.map((date, i) => {
              if (!date) return <div key={`blank-${i}`} aria-hidden />;
              const key = dayKey(date);
              const dayEntries = byDay.get(key) ?? [];
              const holiday = dayEntries.some((e) => e.kind === "holiday");
              const leave = dayEntries.find((e) => e.kind === "leave");
              const working = workingDays.has(weekdayIndex(date)) && !holiday;
              const isToday = key === todayKey;
              const isSelected = key === selected;
              const kinds = Array.from(new Set(dayEntries.map((e) => e.kind)));

              const base =
                "relative flex aspect-square w-full flex-col items-center justify-center rounded-xl text-sm transition focus-visible:outline-offset-0";
              let look: string;
              if (isToday) {
                look = "bg-pme-blue font-bold text-white shadow-pop";
              } else if (leave) {
                look = leave.pending
                  ? "border border-dashed border-emerald-400 bg-emerald-50/60 font-semibold text-emerald-800"
                  : "bg-emerald-50 font-semibold text-emerald-800";
              } else if (working) {
                look = "bg-white font-semibold text-pme-ink ring-1 ring-pme-line/80 hover:ring-pme-blue/50";
              } else {
                look = "font-medium text-gray-400 hover:bg-gray-100";
              }

              const labelParts = [
                dayFmt.format(date),
                working ? t("home.calendar.workingDay") : t("home.calendar.dayOff"),
                ...dayEntries.map((e) => `${kindLabel(e)}: ${e.title}`),
              ];

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(isSelected ? null : key)}
                  aria-pressed={isSelected}
                  aria-label={labelParts.join(". ")}
                  className={`${base} ${look} ${isSelected ? "outline outline-2 outline-pme-navy" : ""}`}
                >
                  <span>{date.getDate()}</span>
                  {kinds.length > 0 && (
                    <span className="absolute bottom-1.5 flex gap-0.5" aria-hidden>
                      {kinds.map((kind) => (
                        <span
                          key={kind}
                          className={`h-1.5 w-1.5 rounded-full ${
                            isToday ? "bg-white" : KIND_STYLE[kind].dot
                          }`}
                        />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] font-medium text-pme-muted">
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="h-3 w-3 rounded-[4px] bg-white ring-1 ring-pme-line" />
              {t("home.calendar.legendWorking")}
            </li>
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-2 rounded-full bg-pme-blue" />
              {t("home.calendar.legendEvent")}
            </li>
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-2 rounded-full bg-pme-lilac" />
              {t("home.calendar.legendHoliday")}
            </li>
            <li className="flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-2 rounded-full bg-emerald-500" />
              {t("home.calendar.legendLeave")}
            </li>
          </ul>
        </div>

        {/* Key dates */}
        <div className="md:col-span-2 md:border-l md:border-pme-line/70 md:pl-5">
          <h3 className="eyebrow mb-3">
            {selectedDate ? dayFmt.format(selectedDate) : t("home.calendar.keyDates")}
          </h3>
          {failed ? (
            <p className="text-sm text-pme-muted">{t("home.calendar.loadError")}</p>
          ) : !isCurrent ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-5/6" />
            </div>
          ) : listed.length === 0 ? (
            <p className="text-sm text-pme-muted">
              {selected ? t("home.calendar.nothingOnDay") : t("home.calendar.noKeyDates")}
            </p>
          ) : (
            <ul className="max-h-[300px] space-y-2.5 overflow-y-auto pr-1">
              {listed.map((e, i) => {
                const a = parseDay(e.start_date);
                const b = parseDay(e.end_date);
                const when =
                  e.start_date === e.end_date
                    ? shortFmt.format(a)
                    : `${shortFmt.format(a)} – ${shortFmt.format(b)}`;
                return (
                  <li key={`${e.kind}-${e.start_date}-${e.title}-${i}`} className="flex gap-3">
                    <span
                      aria-hidden
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${KIND_STYLE[e.kind].dot}`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-snug text-pme-ink">{e.title}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-pme-muted">
                        <span>{when}</span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${KIND_STYLE[e.kind].chip}`}
                        >
                          {kindLabel(e)}
                        </span>
                      </p>
                      {e.description && (
                        <p className="mt-1 text-xs leading-snug text-pme-muted">{e.description}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <Link
            to="/calendar"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-pme-blue hover:underline"
          >
            {t("home.calendar.fullCalendar")}
            <ArrowRight size={15} strokeWidth={2} aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ── Needs your attention ─────────────────────────────────────────────────── */

function AttentionCard({
  attention,
  reviewsEnabled,
  trainingEnabled,
}: {
  attention: HomeAttention | null;
  reviewsEnabled: boolean;
  trainingEnabled: boolean;
}) {
  const { t } = useTranslation();

  const items = useMemo(() => {
    if (!attention) return [];
    const all: {
      key: string;
      count: number;
      to: string;
      icon: LucideIcon;
      review?: boolean;
    }[] = [
      { key: "jobDescription", count: attention.job_description_to_sign ?? 0, to: "/job-description", icon: FileSignature },
      { key: "documentsToSign", count: attention.documents_to_sign, to: "/documents", icon: FileSignature },
      { key: "leaveApprovals", count: attention.leave_approvals, to: "/leave?tab=team", icon: Palmtree },
      { key: "leaveAmendments", count: attention.leave_amendments, to: "/leave?tab=my", icon: Palmtree },
      { key: "trainingDue", count: attention.training_due, to: "/training", icon: GraduationCap },
      { key: "selfAssessments", count: attention.self_assessments, to: "/performance?tab=my-reviews", icon: ClipboardCheck, review: true },
      { key: "colleagueFeedback", count: attention.colleague_feedback, to: "/performance?tab=my-reviews", icon: UsersRound, review: true },
      { key: "managerReviews", count: attention.manager_reviews, to: "/performance?tab=team-reviews", icon: PenLine, review: true },
      { key: "tasks", count: attention.tasks, to: "/performance?tab=tasks", icon: ListChecks },
    ];
    return all.filter(
      (i) =>
        i.count > 0 &&
        (reviewsEnabled || !i.review) &&
        (trainingEnabled || i.key !== "trainingDue"),
    );
  }, [attention, reviewsEnabled, trainingEnabled]);

  return (
    <section aria-label={t("home.attention.title")} className="card p-5">
      <CardTitle icon={ListChecks}>{t("home.attention.title")}</CardTitle>
      {!attention ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-5/6" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex items-center gap-3 rounded-2xl bg-pme-sky/50 px-4 py-4">
          <span className="flex shrink-0 items-start gap-0.5">
            <Sparkle size={22} colour="#0872c1" />
            <Sparkle size={11} colour="#df95ef" className="mt-4" />
          </span>
          <div>
            <p className="text-sm font-bold text-pme-navy">{t("home.attention.allClear")}</p>
            <p className="text-xs text-pme-muted">{t("home.attention.allClearHint")}</p>
          </div>
        </div>
      ) : (
        <ul className="-mx-2">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.key}>
                <Link
                  to={item.to}
                  className="group flex min-h-[48px] items-center gap-3 rounded-xl px-2 py-2 hover:bg-pme-sky/40"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pme-lilac-soft text-[#8a3a9c]">
                    <Icon size={17} strokeWidth={1.75} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-semibold text-pme-ink">
                    {t(`home.attention.${item.key}`, { count: item.count })}
                  </span>
                  <ChevronRight
                    size={17}
                    strokeWidth={2}
                    aria-hidden
                    className="shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-pme-blue"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ── Latest announcement ──────────────────────────────────────────────────── */

function NewsCard({
  announcement,
  loaded,
  lang,
  socialEnabled,
}: {
  announcement: AnnouncementRow | null;
  loaded: boolean;
  lang: string;
  socialEnabled: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const published = useMemo(() => {
    if (!announcement?.published_at) return null;
    return new Intl.DateTimeFormat(lang, {
      day: "numeric",
      month: "long",
    }).format(new Date(announcement.published_at));
  }, [announcement, lang]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && announcement && !announcement.read) {
      // Best-effort read receipt; the card works the same if it fails.
      void api.post(`/api/announcements/${announcement.id}/read`).catch(() => undefined);
    }
  };

  const long = (announcement?.content.length ?? 0) > 150;

  return (
    <section aria-label={t("home.news.title")} className="card flex flex-col p-5">
      <CardTitle icon={Megaphone}>{t("home.news.title")}</CardTitle>
      {!loaded ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ) : !announcement ? (
        <p className="text-sm text-pme-muted">{t("home.news.none")}</p>
      ) : (
        <>
          <p className="text-base font-bold leading-snug text-pme-navy">{announcement.title}</p>
          {published && <p className="mt-0.5 text-xs text-pme-muted">{published}</p>}
          <p
            className={`mt-2 whitespace-pre-line text-sm leading-relaxed text-pme-ink ${
              open ? "" : "line-clamp-3"
            }`}
          >
            {announcement.content}
          </p>
          <div className="mt-auto flex items-center gap-3 pt-3">
            {long && (
              <button
                type="button"
                onClick={toggle}
                aria-expanded={open}
                className="min-h-[44px] text-sm font-semibold text-pme-blue hover:underline"
              >
                {open ? t("home.news.showLess") : t("home.news.readMore")}
              </button>
            )}
            {socialEnabled && (
              <Link
                to="/social"
                className="ml-auto inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-pme-blue hover:underline"
              >
                <MessagesSquare size={15} strokeWidth={2} aria-hidden />
                {t("home.news.seeAll")}
              </Link>
            )}
          </div>
        </>
      )}
    </section>
  );
}
